import type {
  Profile,
  ProfileField,
  ProfileFieldsData,
  ProfileFieldValue,
  ProfileFieldValues,
} from "../types";

const OTHER_OPTION_VALUE = "other";

export function buildProfileFieldRows(
  fields: ProfileField[],
): ProfileField[][] {
  const rows: ProfileField[][] = [];
  let pendingHalfRow: ProfileField[] = [];

  fields.forEach((field) => {
    if (field.width === "full" || isMultiValueField(field)) {
      if (pendingHalfRow.length > 0) {
        rows.push(pendingHalfRow);
        pendingHalfRow = [];
      }
      rows.push([field]);
      return;
    }

    pendingHalfRow.push(field);
    if (pendingHalfRow.length === 2) {
      rows.push(pendingHalfRow);
      pendingHalfRow = [];
    }
  });

  if (pendingHalfRow.length > 0) {
    rows.push(pendingHalfRow);
  }

  return rows;
}

export function readProfileFieldValues(
  fields: ProfileFieldsData,
  profile?: Pick<Profile, "sections">,
): ProfileFieldValues {
  return fields.sections.reduce<ProfileFieldValues>((values, section) => {
    const sectionValues = (profile?.sections ?? []).find(
      (item) => item.title === section.title,
    );

    section.fields.forEach((field) => {
      const value = sectionValues?.fields.find(
        (candidate) => candidate.label === field.label,
      )?.value;

      values[field.fieldKey] = normalizeProfileFieldValue(field, value);
    });

    return values;
  }, {});
}

export function normalizeProfileFieldValue(
  field: ProfileField,
  value: ProfileFieldValue | undefined,
): ProfileFieldValue {
  if (isMultiValueField(field)) {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.filter((item) =>
      fieldAllowsOtherValue(field)
        ? isAllowedOption(field, item) || isCustomOtherValue(field, item)
        : isAllowedOption(field, item),
    );
  }

  if (usesOptions(field)) {
    if (Array.isArray(value)) {
      return (
        value.find((item) =>
          fieldAllowsOtherValue(field)
            ? isAllowedOption(field, item) || isCustomOtherValue(field, item)
            : isAllowedOption(field, item),
        ) ?? null
      );
    }

    if (typeof value !== "string") {
      return null;
    }

    return fieldAllowsOtherValue(field)
      ? isAllowedOption(field, value) || isCustomOtherValue(field, value)
        ? value
        : null
      : isAllowedOption(field, value)
        ? value
        : null;
  }

  return typeof value === "string" ? value : null;
}

export function formatProfileFieldValue(
  field: ProfileField,
  value: ProfileFieldValue | undefined,
): string | null {
  const normalizedValue = normalizeProfileFieldValue(field, value);

  if (Array.isArray(normalizedValue)) {
    if (normalizedValue.length === 0) return null;
    return normalizedValue.map((item) => optionLabel(field, item)).join(", ");
  }

  if (!normalizedValue) return null;
  return optionLabel(field, normalizedValue);
}

export function fieldAllowsOtherValue(field: ProfileField): boolean {
  return field.canSpecifyOther === true && usesOptions(field);
}

export function hasOtherOption(field: ProfileField): boolean {
  return field.options.some((option) => option.value === OTHER_OPTION_VALUE);
}

export function isOtherOptionValue(value: string): boolean {
  return value === OTHER_OPTION_VALUE;
}

export function isCustomOtherValue(
  field: ProfileField,
  value: string,
): boolean {
  return (
    fieldAllowsOtherValue(field) &&
    !isAllowedOption(field, value) &&
    value.trim().length > 0
  );
}

function optionLabel(field: ProfileField, value: string): string {
  return field.options.find((option) => option.value === value)?.label ?? value;
}

function isAllowedOption(field: ProfileField, value: string): boolean {
  return field.options.some((option) => option.value === value);
}

function isMultiValueField(field: ProfileField): boolean {
  return field.type === "multiSelect" || field.type === "checkbox";
}

function usesOptions(field: ProfileField): boolean {
  return (
    field.type === "select" ||
    field.type === "multiSelect" ||
    field.type === "radio" ||
    field.type === "checkbox"
  );
}
